import React, { ReactNode, useMemo, useState } from "react";
import useTranslation from "../../../../../../shared/hooks/useTranslation";
import {
    ProcessedData,
    ProcessedDataResponse,
    ProcessedItem,
    ProcessedParty,
    ProcessedTaxAmount,
} from "../../../../../../services/supervising/processedData/types";

interface Props {
    payload: ProcessedDataResponse | undefined;
    isLoading: boolean;
}

export default function ProcessedDataView({ payload, isLoading }: Props) {
    if (isLoading) return null;
    return payload ? <Loaded payload={payload} /> : <EmptyState />;
}

function EmptyState() {
    const { t } = useTranslation();
    return (
        <p className="text-xs italic text-ink-muted">{t("unit_toolbar.processed_data.empty")}</p>
    );
}

function Loaded({ payload }: { payload: ProcessedDataResponse }) {
    const { t } = useTranslation();
    const [showRaw, setShowRaw] = useState(false);
    const { source, version, data } = payload;
    const rawJson = useMemo(() => JSON.stringify(data, null, 2), [data]);

    return (
        <>
            <div className="mb-2 flex items-center justify-between">
                <div className="text-xs font-medium text-ink-muted">
                    {t("unit_toolbar.processed_data.title")} ({source} v{version})
                </div>
                <button
                    type="button"
                    onClick={() => setShowRaw((v) => !v)}
                    className="text-xs text-ink-muted underline hover:text-ink"
                >
                    {showRaw
                        ? t("unit_toolbar.processed_data.toggle_structured")
                        : t("unit_toolbar.processed_data.toggle_raw")}
                </button>
            </div>
            {showRaw ? (
                <pre className="max-h-96 overflow-auto rounded-md bg-surface-2 p-3 text-xs text-ink">
                    {rawJson}
                </pre>
            ) : (
                <StructuredView data={data} />
            )}
        </>
    );
}

function StructuredView({ data }: { data: ProcessedData }) {
    const { t } = useTranslation();
    const tl = (key: string) => t(`unit_toolbar.processed_data.${key}`);

    return (
        <div className="space-y-4 text-sm">
            <Section title={tl("sections.general")}>
                <Field label={tl("fields.invoice_number")} value={data.invoiceNumber} />
                <Field label={tl("fields.issue_date")} value={data.issueDate} />
                <Field label={tl("fields.tax_payment_date")} value={data.taxPaymentDate} />
                <Field label={tl("fields.currency")} value={data.currencyCode} />
                <Field label={tl("fields.document_type")} value={data.documentType} />
                <Field label={tl("fields.category")} value={data.category} />
                <Field label={tl("fields.note")} value={data.note} />
            </Section>
            <PartyBlock title={tl("sections.supplier")} party={data.supplier} />
            <PartyBlock title={tl("sections.customer")} party={data.customer} />
            <Section title={tl("sections.amounts")}>
                <Field label={tl("fields.total")} value={data.amounts?.totalAmount} />
                <Field
                    label={tl("fields.tax_exclusive")}
                    value={data.amounts?.totalAmountTaxExclusive}
                />
                {data.amounts?.taxAmounts?.length ? (
                    <TaxAmountsTable amounts={data.amounts.taxAmounts} />
                ) : null}
            </Section>
            <Section title={tl("sections.payment")}>
                <Field label={tl("fields.iban")} value={data.payment?.iban} />
                <Field label={tl("fields.variable_symbol")} value={data.payment?.variableSymbol} />
                <Field label={tl("fields.specific_symbol")} value={data.payment?.specificSymbol} />
                <Field label={tl("fields.constant_symbol")} value={data.payment?.constantSymbol} />
                <Field label={tl("fields.due_date")} value={data.payment?.dueDate} />
            </Section>
            {data.items?.length ? <ItemsTable items={data.items} /> : null}
        </div>
    );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
    return (
        <div>
            <SectionTitle>{title}</SectionTitle>
            <dl className="grid grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-y-1">{children}</dl>
        </div>
    );
}

function SectionTitle({ children }: { children: ReactNode }) {
    return (
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">
            {children}
        </div>
    );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
    if (value === null || value === undefined || value === "") return null;
    return (
        <>
            <dt className="text-ink-muted">{label}</dt>
            <dd className="break-words text-ink">{value}</dd>
        </>
    );
}

function PartyBlock({ title, party }: { title: string; party?: ProcessedParty | null }) {
    const { t } = useTranslation();
    const tl = (key: string) => t(`unit_toolbar.processed_data.${key}`);

    if (!party) {
        return (
            <div>
                <SectionTitle>{title}</SectionTitle>
                <p className="text-xs italic text-ink-muted">{tl("placeholders.missing")}</p>
            </div>
        );
    }
    const vatin = party.taxScheme?.find((s) => s.scheme === "VAT")?.companyId;
    return (
        <Section title={title}>
            <Field label={tl("fields.name")} value={party.name} />
            <Field label={tl("fields.vat_id")} value={vatin} />
            <Field label={tl("fields.ico")} value={party.id} />
            <Field label={tl("fields.address")} value={formatAddress(party.postalAddress)} />
            <Field label={tl("fields.contact")} value={party.contactId} />
        </Section>
    );
}

function formatAddress(address?: ProcessedParty["postalAddress"]): string | null {
    if (!address) return null;
    const parts = [
        [address.streetName, address.buildingNumber].filter(Boolean).join(" "),
        [address.postalZone, address.cityName].filter(Boolean).join(" "),
        address.countryName ?? address.countryCode,
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
}

function TaxAmountsTable({ amounts }: { amounts: ProcessedTaxAmount[] }) {
    const { t } = useTranslation();
    const tl = (key: string) => t(`unit_toolbar.processed_data.${key}`);

    return (
        <>
            <dt className="col-span-2 mt-2 text-xs font-medium text-ink-muted">
                {tl("sections.vat_breakdown")}
            </dt>
            <dd className="col-span-2">
                <table className="w-full text-xs">
                    <thead className="text-ink-muted">
                        <tr>
                            <th className="text-left font-normal">{tl("fields.rate")}</th>
                            <th className="text-right font-normal">{tl("fields.amount")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {amounts.map((row, idx) => (
                            <tr key={idx} className="text-ink">
                                <td>{row.percent ?? "—"}%</td>
                                <td className="text-right">{row.amount ?? "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </dd>
        </>
    );
}

function ItemsTable({ items }: { items: ProcessedItem[] }) {
    const { t } = useTranslation();
    const tl = (key: string) => t(`unit_toolbar.processed_data.${key}`);

    return (
        <div>
            <SectionTitle>
                {tl("sections.items")} ({items.length})
            </SectionTitle>
            <ul className="space-y-2">
                {items.map((item, idx) => (
                    <li key={idx} className="rounded-md border border-border p-2 text-xs">
                        <div className="text-ink">
                            {item.description?.trim() || tl("placeholders.no_description")}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-ink-muted">
                            {item.quantity !== null && item.quantity !== undefined && (
                                <span>
                                    {tl("fields.quantity")}: {item.quantity}
                                </span>
                            )}
                            {item.unitPrice !== null && item.unitPrice !== undefined && (
                                <span>
                                    {tl("fields.unit_price")}: {item.unitPrice}
                                </span>
                            )}
                            {item.totalPrice !== null && item.totalPrice !== undefined && (
                                <span>
                                    {tl("fields.line_total")}: {item.totalPrice}
                                </span>
                            )}
                            {item.percent && (
                                <span>
                                    {tl("fields.vat")}: {item.percent}%
                                </span>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}
